import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";

type Category = 'sql' | 'python' | 'statistics' | 'bi' | 'kpis' | 'google_analytics' | 'google_tag_manager' | 'looker_studio' | 'power_bi' | 'data_engineering' | 'machine_learning' | 'excel_advanced' | 'cloud_aws' | 'cloud_azure' | 'cloud_gcp';

interface CategoryStats {
  category: Category;
  totalQuestions: number;
  beginnerCount: number;
  intermediateCount: number;
  advancedCount: number;
  userAttempts: number;
  userCorrect: number;
  userAccuracy: number;
}

interface UserMetrics {
  totalAttempts: number;
  totalCorrect: number;
  accuracy: number;
  averageTime: number;
  totalPoints: number;
}

export function useQuizStats() {
  const user = useUser();
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [userMetrics, setUserMetrics] = useState<UserMetrics>({
    totalAttempts: 0,
    totalCorrect: 0,
    accuracy: 0,
    averageTime: 0,
    totalPoints: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [user?.id]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Buscar todas as questões agrupadas por categoria e dificuldade
      const { data: quizzes, error: quizzesError } = await supabase
        .from('quizzes')
        .select('category, difficulty')
        .eq('is_active', true);

      if (quizzesError) throw quizzesError;

      // Agrupar por categoria
      const categories: Category[] = ['sql', 'python', 'statistics', 'google_analytics', 'google_tag_manager', 'looker_studio', 'power_bi', 'data_engineering', 'machine_learning', 'excel_advanced', 'cloud_aws', 'cloud_azure', 'cloud_gcp'];
      
      const stats: CategoryStats[] = categories.map(cat => {
        const catQuizzes = quizzes?.filter(q => q.category === cat) || [];
        return {
          category: cat,
          totalQuestions: catQuizzes.length,
          beginnerCount: catQuizzes.filter(q => q.difficulty === 'beginner').length,
          intermediateCount: catQuizzes.filter(q => q.difficulty === 'intermediate').length,
          advancedCount: catQuizzes.filter(q => q.difficulty === 'advanced').length,
          userAttempts: 0,
          userCorrect: 0,
          userAccuracy: 0,
        };
      });

      // Se usuário logado, buscar tentativas
      if (user?.id) {
        const { data: attempts, error: attemptsError } = await supabase
          .from('quiz_attempts')
          .select('category, is_correct, time_spent_seconds, points_earned')
          .eq('user_id', user.id);

        if (!attemptsError && attempts) {
          // Atualizar stats por categoria
          stats.forEach(stat => {
            const catAttempts = attempts.filter(a => a.category === stat.category);
            stat.userAttempts = catAttempts.length;
            stat.userCorrect = catAttempts.filter(a => a.is_correct).length;
            stat.userAccuracy = stat.userAttempts > 0 
              ? Math.round((stat.userCorrect / stat.userAttempts) * 100) 
              : 0;
          });

          // Calcular métricas gerais do usuário
          const totalAttempts = attempts.length;
          const totalCorrect = attempts.filter(a => a.is_correct).length;
          const totalTime = attempts.reduce((sum, a) => sum + (a.time_spent_seconds || 0), 0);
          const totalPoints = attempts.reduce((sum, a) => sum + (a.points_earned || 0), 0);

          setUserMetrics({
            totalAttempts,
            totalCorrect,
            accuracy: totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0,
            averageTime: totalAttempts > 0 ? Math.round(totalTime / totalAttempts) : 0,
            totalPoints,
          });
        }
      }

      setCategoryStats(stats);
    } catch (error) {
      console.error('Error fetching quiz stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return { categoryStats, userMetrics, loading, refetch: fetchStats };
}
